/**
 * Pipeline Configuration Utility
 * Loads and caches pipeline configuration from the backend
 */

let configCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Load pipeline configuration from backend
 * @param {string} tenantCode - Optional tenant code
 * @returns {Promise<object>} Pipeline configuration
 */
export async function loadPipelineConfig(tenantCode = null) {
  try {
    const cacheKey = tenantCode || 'global';
    
    // Check cache
    if (configCache && cacheTimestamp) {
      const cacheAge = Date.now() - cacheTimestamp;
      if (cacheAge < CACHE_DURATION) {
        return configCache;
      }
    }

    const response = await fetch('/api/pipeline-config', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load pipeline config: ${response.statusText}`);
    }

    const data = await response.json();
    configCache = data.config || data;
    cacheTimestamp = Date.now();
    
    return configCache;
  } catch (error) {
    console.error('Error loading pipeline config:', error);
    // Return default config on error
    return getDefaultConfig();
  }
}

/**
 * Load pipeline stages
 * @param {string} tenantCode - Optional tenant code
 * @returns {Promise<Array>} Array of pipeline stages
 */
export async function loadPipelineStages(tenantCode = null) {
  try {
    const response = await fetch('/api/pipeline-config/stages', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load pipeline stages: ${response.statusText}`);
    }

    const data = await response.json();
    return data.stages || [];
  } catch (error) {
    console.error('Error loading pipeline stages:', error);
    return getDefaultStages();
  }
}

/**
 * Load kanban stages for a specific entity type
 * @param {string} entityType - Entity type (e.g., 'opportunity')
 * @param {string} tenantCode - Optional tenant code
 * @returns {Promise<Array>} Array of kanban stage options
 */
export async function loadKanbanStages(entityType, tenantCode = null) {
  try {
    const response = await fetch(`/api/pipeline-config/kanban-stages/${entityType}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load kanban stages: ${response.statusText}`);
    }

    const data = await response.json();
    return data.kanbanStages || [];
  } catch (error) {
    console.error('Error loading kanban stages:', error);
    return getDefaultKanbanStages(entityType);
  }
}

/**
 * Load opportunity stage mapping
 * @param {string} tenantCode - Optional tenant code
 * @returns {Promise<object>} Opportunity stage mapping
 */
export async function loadOpportunityStageMapping(tenantCode = null) {
  try {
    const response = await fetch('/api/pipeline-config/opportunity-stage-mapping', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load opportunity stage mapping: ${response.statusText}`);
    }

    const data = await response.json();
    return data.mapping || {};
  } catch (error) {
    console.error('Error loading opportunity stage mapping:', error);
    return getDefaultOpportunityStageMapping();
  }
}

/**
 * Clear the configuration cache
 */
export function clearPipelineConfigCache() {
  configCache = null;
  cacheTimestamp = null;
}

/**
 * Save pipeline configuration
 * @param {object} config - Pipeline configuration to save
 * @param {string} tenantCode - Optional tenant code
 * @returns {Promise<object>} Saved configuration
 */
export async function savePipelineConfig(config, tenantCode = null) {
  try {
    const response = await fetch('/api/pipeline-config', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      },
      body: JSON.stringify(config)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to save pipeline config: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Clear cache after successful save
    clearPipelineConfigCache();
    
    return data.config || data;
  } catch (error) {
    console.error('Error saving pipeline config:', error);
    throw error;
  }
}

/**
 * Get available icons
 * @returns {Promise<Array>} Array of available icon names
 */
export async function getAvailableIcons() {
  try {
    const response = await fetch('/api/pipeline-config/icons', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load icons: ${response.statusText}`);
    }

    const data = await response.json();
    return data.icons || [];
  } catch (error) {
    console.error('Error loading icons:', error);
    return [];
  }
}

/**
 * Get default configuration (fallback)
 * @returns {object} Default pipeline configuration
 */
function getDefaultConfig() {
  return {
    stages: getDefaultStages(),
    opportunityStageMapping: getDefaultOpportunityStageMapping(),
    kanbanStages: {
      opportunity: getDefaultKanbanStages('opportunity')
    }
  };
}

/**
 * Get default stages (fallback)
 * @returns {Array} Default pipeline stages
 */
function getDefaultStages() {
  return [
    {
      id: 'lead',
      displayName: 'Lead',
      icon: 'Target',
      order: 1,
      enabled: true
    },
    {
      id: 'opportunities',
      displayName: 'Opportunities',
      icon: 'Trophy',
      order: 2,
      enabled: true
    },
    {
      id: 'proposal',
      displayName: 'Proposal',
      icon: 'FileText',
      order: 3,
      enabled: true
    },
    {
      id: 'negotiate',
      displayName: 'Negotiate',
      icon: 'MessageCircle',
      order: 4,
      enabled: true
    },
    {
      id: 'won',
      displayName: 'Won',
      icon: 'CheckCircle',
      order: 5,
      enabled: true
    },
    {
      id: 'lost',
      displayName: 'Lost',
      icon: 'XCircle',
      order: 6,
      enabled: true
    }
  ];
}

/**
 * Get default opportunity stage mapping (fallback)
 * @returns {object} Default mapping
 */
function getDefaultOpportunityStageMapping() {
  return {
    lead: ['Lead', 'Qualified'],
    opportunities: ['Lead', 'Qualified'],
    proposal: ['Proposal'],
    negotiate: ['Negotiation'],
    won: ['Closed Won'],
    lost: ['Closed Lost']
  };
}

/**
 * Get default kanban stages for entity type (fallback)
 * @param {string} entityType - Entity type
 * @returns {Array} Default kanban stages
 */
function getDefaultKanbanStages(entityType) {
  if (entityType === 'opportunity') {
    return [
      { value: 'Prospecting', label: 'Prospecting', order: 1 },
      { value: 'Qualification', label: 'Qualification', order: 2 },
      { value: 'Needs Analysis', label: 'Needs Analysis', order: 3 },
      { value: 'Value Proposition', label: 'Value Proposition', order: 4 },
      { value: 'Id. Decision Makers', label: 'Id. Decision Makers', order: 5 },
      { value: 'Perception Analysis', label: 'Perception Analysis', order: 6 },
      { value: 'Proposal/Price Quote', label: 'Proposal/Price Quote', order: 7 },
      { value: 'Negotiation/Review', label: 'Negotiation/Review', order: 8 },
      { value: 'Closed Won', label: 'Closed Won', order: 9 },
      { value: 'Closed Lost', label: 'Closed Lost', order: 10 }
    ];
  }
  return [];
}

